import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { computeSoVRollup, useProjectBudget } from "@/hooks/use-budgets";
import type { ChangeOrderSovContributor } from "@/hooks/use-change-orders";
import { useChangeOrderSovRollup } from "@/hooks/use-change-orders";
import { formatCurrency, formatPercent } from "@/lib/formatters";

interface SoVTabProps {
  projectId: string;
}

/**
 * Schedule of Values tab — read-only roll-up of Buy Log line items by cost
 * code, grouped by CSI Division.
 *
 * This is the GC's internal mirror of the owner-facing SoV (spec §5.4).
 * Reuses `useProjectBudget` + the pure `computeSoVRollup` reducer — no new
 * API and no mutations.
 */
export function SoVTab({ projectId }: SoVTabProps) {
  const budget = useProjectBudget(projectId);
  const coRollup = useChangeOrderSovRollup(projectId);
  const rollup = useMemo(
    () => computeSoVRollup(budget.data?.lineItems ?? []),
    [budget.data?.lineItems],
  );
  const coByCostCode = coRollup.data ?? {};
  const totalChangeOrders = Object.values(coByCostCode).reduce((s, c) => s + c.total, 0);

  const [drill, setDrill] = useState<{
    costCode: string;
    description: string;
    contributors: ChangeOrderSovContributor[];
  } | null>(null);

  if (budget.isLoading) return <Skeleton className="h-96 w-full" />;
  if (budget.isError) {
    return <ErrorState message={budget.error?.message} onRetry={() => budget.refetch()} />;
  }

  if (rollup.groups.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
        <p className="text-sm text-gray-500">
          No Buy Log line items yet — add lines on the Buy Log tab to see the Schedule of Values
          rollup here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top summary */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <SoVMetric label="Scheduled Value" value={formatCurrency(rollup.totalScheduledValue)} />
        <SoVMetric
          label="Change Orders"
          value={formatCurrency(totalChangeOrders)}
          tone={totalChangeOrders > 0 ? "positive" : totalChangeOrders < 0 ? "negative" : "neutral"}
        />
        <SoVMetric label="Total Awarded" value={formatCurrency(rollup.totalAward)} />
        <SoVMetric
          label="Variance"
          value={formatCurrency(rollup.totalVariance)}
          tone={
            rollup.totalVariance > 0
              ? "positive"
              : rollup.totalVariance < 0
                ? "negative"
                : "neutral"
          }
        />
        <SoVMetric label="% Awarded" value={formatPercent(rollup.percentAwarded)} />
      </div>

      {/* Rollup table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Cost Code
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Description
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                Scheduled Value
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                Change Order
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                Total Awarded
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                Variance
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                % Awarded
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rollup.groups.map((group) => {
              const varianceClass =
                group.variance > 0
                  ? "text-emerald-600"
                  : group.variance < 0
                    ? "text-red-600"
                    : "text-gray-900";
              const groupCoTotal = group.rows.reduce(
                (s, r) => s + (coByCostCode[r.costCodeId]?.total ?? 0),
                0,
              );
              return [
                <TableRow key={`header-${group.divisionKey}`} className="bg-gray-50/80">
                  <TableCell colSpan={2} className="text-sm font-semibold text-gray-900">
                    {group.divisionLabel}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      ({group.rows.length} cost code{group.rows.length === 1 ? "" : "s"})
                    </span>
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(group.scheduledValue)}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-gray-900 text-right">
                    {groupCoTotal !== 0 ? formatCurrency(groupCoTotal) : "--"}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(group.totalAward)}
                  </TableCell>
                  <TableCell className={`text-sm font-semibold text-right ${varianceClass}`}>
                    {formatCurrency(group.variance)}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-gray-900 text-right">
                    {formatPercent(
                      group.scheduledValue > 0 ? group.totalAward / group.scheduledValue : 0,
                    )}
                  </TableCell>
                </TableRow>,
                ...group.rows.map((row) => {
                  const rowVarianceClass =
                    row.variance > 0
                      ? "text-emerald-600"
                      : row.variance < 0
                        ? "text-red-600"
                        : "text-gray-900";
                  const coEntry = coByCostCode[row.costCodeId];
                  const coAmount = coEntry?.total ?? 0;
                  return (
                    <TableRow key={row.costCodeId} className="hover:bg-gray-50">
                      <TableCell className="text-sm font-medium text-gray-900 whitespace-nowrap pl-8">
                        {row.code}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700">{row.description}</TableCell>
                      <TableCell className="text-sm text-gray-900 text-right whitespace-nowrap">
                        {formatCurrency(row.scheduledValue)}
                      </TableCell>
                      <TableCell className="text-sm text-right whitespace-nowrap">
                        {coAmount !== 0 && coEntry ? (
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            onClick={() =>
                              setDrill({
                                costCode: `${row.code} — ${row.description}`,
                                description: row.description,
                                contributors: coEntry.contributors,
                              })
                            }
                          >
                            {formatCurrency(coAmount)}
                          </button>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-900 text-right whitespace-nowrap">
                        {formatCurrency(row.totalAward)}
                      </TableCell>
                      <TableCell
                        className={`text-sm text-right whitespace-nowrap ${rowVarianceClass}`}
                      >
                        {formatCurrency(row.variance)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-900 text-right whitespace-nowrap">
                        {formatPercent(row.percentAwarded)}
                      </TableCell>
                    </TableRow>
                  );
                }),
              ];
            })}
            {/* Grand total */}
            <TableRow className="bg-gray-900 hover:bg-gray-900">
              <TableCell colSpan={2} className="text-sm font-semibold text-white">
                Grand Total
              </TableCell>
              <TableCell className="text-sm font-semibold text-white text-right">
                {formatCurrency(rollup.totalScheduledValue)}
              </TableCell>
              <TableCell className="text-sm font-semibold text-white text-right">
                {totalChangeOrders !== 0 ? formatCurrency(totalChangeOrders) : "--"}
              </TableCell>
              <TableCell className="text-sm font-semibold text-white text-right">
                {formatCurrency(rollup.totalAward)}
              </TableCell>
              <TableCell
                className={`text-sm font-semibold text-right ${
                  rollup.totalVariance > 0
                    ? "text-emerald-300"
                    : rollup.totalVariance < 0
                      ? "text-red-300"
                      : "text-white"
                }`}
              >
                {formatCurrency(rollup.totalVariance)}
              </TableCell>
              <TableCell className="text-sm font-semibold text-white text-right">
                {formatPercent(rollup.percentAwarded)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <Dialog open={drill !== null} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change Orders · {drill?.costCode}</DialogTitle>
          </DialogHeader>
          {drill && (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-gray-500">
                      CO #
                    </TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-gray-500">
                      Title / Line
                    </TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-gray-500">
                      Approved
                    </TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-gray-500 text-right">
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drill.contributors.map((c) => (
                    <TableRow key={`${c.id}-${c.lineDescription}`}>
                      <TableCell className="text-sm font-medium text-gray-900">
                        {c.commitmentNumber}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700">
                        <div className="font-medium">{c.title}</div>
                        <div className="text-xs text-gray-500">{c.lineDescription}</div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {c.approvedAt ? new Date(c.approvedAt).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-sm text-right font-medium">
                        {formatCurrency(c.amount, { precise: true })}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3} className="text-sm">
                      Total
                    </TableCell>
                    <TableCell className="text-sm text-right">
                      {formatCurrency(
                        drill.contributors.reduce((s, c) => s + c.amount, 0),
                        { precise: true },
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SoVMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
}) {
  const valueClass =
    tone === "positive"
      ? "text-emerald-600"
      : tone === "negative"
        ? "text-red-600"
        : "text-gray-900";
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}
